import { useMantineTheme } from '@mantine/core';
import { useCallback } from 'react';
import { useChat } from 'src/hooks/useChat';
import { useJobs } from 'src/hooks/useJobs';
import { printTranscript } from 'src/app/chat/printTranscript';
import { resolvePrintChartColors } from 'src/app/chat/printChartPlan';
import {
  buildChatTranscript,
  buildTranscriptMarkdown,
  transcriptFileStem,
} from 'src/app/chat/transcript';
import {
  getErrorMessage,
  logger,
  showErrorToast,
  triggerDownload,
} from 'src/utils';

export interface IChatTranscriptExport {
  canExport: boolean;
  downloadMarkdown: () => void;
  print: () => void;
}

/** @registry Export actions for the open chat conversation: Markdown download and whole-transcript print; `canExport` is false with no saved conversation, no messages, or a reply still streaming. */
export const useChatTranscriptExport = (): IChatTranscriptExport => {
  const {
    conversations,
    conversationId,
    messages,
    streaming,
    loadingConversation,
  } = useChat();
  const { jobs } = useJobs();
  const theme = useMantineTheme();

  const canExport =
    !!conversationId &&
    messages.length > 0 &&
    !streaming &&
    !loadingConversation;

  const downloadMarkdown = useCallback(() => {
    try {
      const transcript = buildChatTranscript(
        conversations.find((c) => c.id === conversationId),
        jobs,
        messages,
      );
      const markdown = buildTranscriptMarkdown(transcript, {
        appOrigin: window.location.origin,
      });
      const blob = new Blob([markdown], {
        type: 'text/markdown;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      triggerDownload(
        url,
        `${transcriptFileStem(transcript.title, transcript.exportedAt)}.md`,
      );
      // The anchor click starts the download synchronously; revoking on the next
      // macrotask keeps the URL alive long enough for the browser to read it.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      logger.error('Failed to export chat transcript', { err });
      showErrorToast(getErrorMessage(err));
    }
  }, [conversations, conversationId, jobs, messages]);

  const print = useCallback(() => {
    try {
      const transcript = buildChatTranscript(
        conversations.find((c) => c.id === conversationId),
        jobs,
        messages,
      );
      printTranscript(transcript, {
        appOrigin: window.location.origin,
        documentTitle: transcriptFileStem(
          transcript.title,
          transcript.exportedAt,
        ),
        palette: {
          text: theme.black,
          dimmed: theme.colors.gray[7],
          border: theme.colors.gray[4],
          surface: theme.colors.gray[1],
          paper: theme.white,
          chartColors: resolvePrintChartColors(theme),
          fontFamily: theme.fontFamily,
          monospaceFontFamily: theme.fontFamilyMonospace,
        },
      });
    } catch (err) {
      logger.error('Failed to print chat transcript', { err });
      showErrorToast(getErrorMessage(err));
    }
  }, [conversations, conversationId, jobs, messages, theme]);

  return { canExport, downloadMarkdown, print };
};
